import clsx from 'clsx';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';
import { Logo, ProgressFlow } from '@care/react-component-lib';
import { CLIENT_FEATURE_FLAGS, CZEN_BASE_PATH, VERTICALS_NAMES } from '@/constants';
import { YesOrNoAnswer } from '@/__generated__/globalTypes';
import { getFeatureFlagValues } from '@/utilities/featureFlagHelper';
import getVertical from '@/utilities/verticalUtils';
import { urlHelper } from '@/utilities/urlHelper';
import useIsSinglePageEnrollment from '@/components/hooks/useIsSinglePageEnrollment';
import { useCondensedAccountCreation } from '@/components/hooks/useCondensedAccountCreation';
import useFlowHelper from '@/components/hooks/useFlowHelper';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import { FlowType, SxClassProps } from '@/types/common';
import { useSeekerState } from './AppState';
import useMVEnrollmentSteps from './hooks/useMVEnrollmentSteps';

const {
  publicRuntimeConfig: { CZEN_GENERAL },
} = getConfig();

const classes: SxClassProps = {
  wrapper: (theme) => ({
    '& .progress-flow-status': {
      borderBottom: 0,
    },
    '&.bottom-border': {
      borderBottom: `1px solid ${theme.palette.care?.grey[300]}`,
    },
  }),
};

const { TTP_ASSET_URL } = CLIENT_FEATURE_FLAGS;

const ProgressFlowNavbar = () => {
  const { pathname } = useRouter();
  const vertical = getVertical(pathname);
  const {
    stepNumber,
    totalSteps,
    currentFlow,
    hideStepNumber,
    hideProgressIndicator,
    showBottomBorder,
  } = useFlowHelper();
  const { isSeekerSinglePageEnrollment } = useIsSinglePageEnrollment();
  const { condensedAccountCreateFlag } = useCondensedAccountCreation();
  const { isUserAssignedToSkipSteps } = useSeekerState();
  const { mvHideSteps, mvLabel, mvTotalSteps } = useMVEnrollmentSteps({ stepNumber });

  const [logoUrl] = getFeatureFlagValues<[string]>([TTP_ASSET_URL]);
  const altLogoUrl = urlHelper(logoUrl) || `${CZEN_GENERAL}/s/d/ttp/care-logo.svg`;

  const condensedAccountCreateFlagValue = condensedAccountCreateFlag?.value === 2;
  const hideForCondensedAccountCreation =
    condensedAccountCreateFlagValue && vertical === VERTICALS_NAMES.CHILD_CARE;

  let label = '';

  let hideSteps =
    hideForCondensedAccountCreation ||
    isSeekerSinglePageEnrollment ||
    hideStepNumber ||
    !currentFlow ||
    isUserAssignedToSkipSteps === YesOrNoAnswer.YES;

  let totalStepsNumber = totalSteps;

  if (currentFlow === FlowType.PROVIDER_MV_DETAILS) {
    label = mvLabel;
    hideSteps = mvHideSteps;
    totalStepsNumber = mvTotalSteps;
  }

  if (currentFlow === FlowType.PROVIDER_MV_ACCOUNT_CREATION) {
    label = 'Create your account';
    totalStepsNumber = 3;
  }

  const logEvent = () => {
    AnalyticsHelper.logEvent({
      name: 'Consumer Logo Clicked',
      data: {
        screen_name: 'Single Page Enrollment',
      },
    });
  };

  return (
    <Box sx={classes.wrapper} className={clsx(showBottomBorder && 'bottom-border')}>
      <ProgressFlow
        step={stepNumber}
        total={totalStepsNumber}
        label={label}
        hideSteps={hideSteps}
        hideProgress={hideProgressIndicator}
        logo={
          <Box
            component="div"
            onKeyDown={(e) => {
              if (isSeekerSinglePageEnrollment && (e.key === 'Enter' || e.key === 'NumpadEnter')) {
                logEvent();
              }
            }}
            tabIndex={0}
            role="button"
            data-testid="care-logo"
            onClick={() => {
              if (isSeekerSinglePageEnrollment) {
                logEvent();
              }
            }}>
            <Logo altLogoUrl={altLogoUrl} variant="careAlt" />
          </Box>
        }
        link={isSeekerSinglePageEnrollment ? CZEN_BASE_PATH : ''}
      />
    </Box>
  );
};

ProgressFlowNavbar.defaultProps = {
  currentFlow: '',
};

export default ProgressFlowNavbar;
